import { graphql } from 'react-relay';
import { collectorTabConfig_viewer$data as ViewerType } from './__generated__/collectorTabConfig_viewer.graphql';

export type TabConfig = {
    path: string;
    isVisible: boolean;
    isServerSide: boolean;
};

// fragment used in root profile queries
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fragment = graphql`
    fragment collectorTabConfig_viewer on Viewer {
        creations: nftItemSearch(
            creatorProfileUsernames: [$username]
            creatorAddresses: [$walletAddress]
            onlyFirstdibsContracts: true
        ) {
            totalResults
        }
        collection: nftItemSearch(
            ownerProfileUsernames: [$username]
            ownerAddresses: [$walletAddress]
            excludeOwnerCreatedItems: true
        ) {
            totalResults
        }
        liveAuctions: nftItemSearch(
            auctionSellerProfileUsernames: [$username]
            auctionSellerAddresses: [$walletAddress]
            excludeOwnerCreatedItems: true
        ) {
            totalResults
        }
        liveBids: nftAuctionsByBidder(
            username: $username
            bidderAddress: $walletAddress
            status: BIDDER_LIVE
        ) {
            edges {
                __typename
            }
        }
        lostBids: nftAuctionsByBidder(
            username: $username
            bidderAddress: $walletAddress
            status: BIDDER_LOST
        ) {
            edges {
                __typename
            }
        }
        draftItems: nftItemDraftsByCreator(username: $username, creatorAddress: $walletAddress)
            @include(if: $fetchDrafts) {
            edges {
                __typename
            }
        }
        tokenImports: nftTokenContractImports(username: $username, creatorAddress: $walletAddress) {
            totalResults
            edges {
                node {
                    ...importCollectionsConfig_contractImport
                }
            }
        }
    }
`;

export interface ViewerInputInterface {
    creations: ViewerType['creations'];
    collection: ViewerType['collection'];
    liveAuctions: ViewerType['liveAuctions'];
    liveBids: ViewerType['liveBids'];
    lostBids: ViewerType['lostBids'];
    draftItems?: ViewerType['draftItems'];
    tokenImports?: ViewerType['tokenImports'];
}

function setFirstVisibleAsRoot(tabConfig: TabConfig[]): void {
    const firstVisibleServerSideTab = tabConfig.find(
        ({ isVisible, isServerSide }) => isVisible && isServerSide
    );

    if (firstVisibleServerSideTab) {
        firstVisibleServerSideTab.path = '/';
    }
}

/* used for picking correct initial tab path for SSR in the route and in collector tab config */
export function collectorTabConfig(
    viewer: ViewerInputInterface,
    enableDrafts: boolean | undefined = false
): TabConfig[] {
    const { creations, collection, liveAuctions, liveBids, lostBids, draftItems, tokenImports } =
        viewer;

    const config = [
        {
            path: '/drafts',
            isVisible: !!draftItems?.edges?.length && enableDrafts,
            isServerSide: false,
        },
        {
            path: '/creations',
            isVisible: !!creations?.totalResults || !!tokenImports?.totalResults,
            isServerSide: true,
        },
        {
            path: '/collection',
            isVisible: !!collection?.totalResults || !!liveAuctions?.totalResults,
            isServerSide: true,
        },
        {
            path: '/bids',
            isVisible: !!liveBids?.edges?.length || !!lostBids?.edges?.length,
            isServerSide: true,
        },
    ];

    setFirstVisibleAsRoot(config);

    return config;
}
